'use client';

import React, { createContext, useContext, ReactNode, useEffect, useState } from 'react';
import { supabase, auth, db, User } from '@/lib/supabase';
import { Session, User as SupabaseUser } from '@supabase/supabase-js';
import { generateRandomAvatar } from '@/components/AvatarCreator';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  signUp: (email: string, password: string, firstName: string, lastName: string) => Promise<{ error: any }>;
  signIn: (email: string, password: string) => Promise<{ error: any }>;
  signOut: () => Promise<{ error: any }>;
  updateProfile: (updates: Partial<User>) => Promise<{ error: any }>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Only run on client side
    if (typeof window === 'undefined') {
      // Keep loading true on server side to prevent SSR mismatches
      return;
    }

    // Failsafe: Stop loading after 10 seconds with session validation
    const failsafeTimeout = setTimeout(async () => {
      console.warn('AUTH: Failsafe timeout - checking session state');
      const { session } = await auth.getSession();
      if (!session) {
        setIsLoading(false);
        setSession(null);
        setUser(null);
      } else {
        setIsLoading(false);
      }
    }, 10000);

    // Get initial session - simplified version
    const getInitialSession = async () => {
      try {
        const { session, error } = await auth.getSession();
        
        if (error) {
          console.error('AUTH: Session recovery error:', error);
        }
        
        setSession(session);
        
        if (session?.user) {
          // Load profile asynchronously without blocking
          loadUserProfile(session.user).catch(error => {
            console.error('AUTH: Profile loading failed (non-blocking):', error);
          });
        }
      } catch (error: any) {
        console.error('AUTH: Session recovery failed:', error);
        setSession(null);
        setUser(null);
      } finally {
        // Always complete loading after session check
        clearTimeout(failsafeTimeout);
        setIsLoading(false);
      }
    };

    getInitialSession();

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        setSession(session);
        
        if (session?.user) {
          // Load profile asynchronously without blocking
          loadUserProfile(session.user).catch(error => {
            console.error('AUTH: Profile loading failed in auth change:', error);
          });
        } else {
          setUser(null);
        }
      }
    );

    return () => {
      subscription.unsubscribe();
      clearTimeout(failsafeTimeout);
    };
  }, []);
  const loadUserProfile = async (supabaseUser: SupabaseUser) => {
    try {
      // Create a basic user object immediately as fallback
      const firstName = supabaseUser.user_metadata?.first_name || 
                       supabaseUser.user_metadata?.firstName || 
                       supabaseUser.email?.split('@')[0] || 
                       'User';
      const lastName = supabaseUser.user_metadata?.last_name || 
                      supabaseUser.user_metadata?.lastName || 
                      '';
      
      const basicUser: User = {
        id: supabaseUser.id,
        email: supabaseUser.email || '',
        first_name: firstName,
        last_name: lastName,
        avatar_url: supabaseUser.user_metadata?.avatar_url,
        avatar_data: supabaseUser.user_metadata?.avatar_data || null,
        created_at: supabaseUser.created_at,
        updated_at: new Date().toISOString()
      };
      
      // Set the basic user immediately to prevent blocking
      setUser(basicUser);
      
      const { data: profile, error } = await db.getProfile(supabaseUser.id);
      
      if (error) {
        if (error.code === 'PGRST116') {
          
          // Try to create the profile using the metadata
          const firstName = supabaseUser.user_metadata?.first_name || '';
          const lastName = supabaseUser.user_metadata?.last_name || '';
          
          // Generate random avatar for new user
          const randomAvatar = generateRandomAvatar();
          
          // Call the create_user_profile function
          const { error: createError } = await supabase.rpc('create_user_profile', {
            user_id: supabaseUser.id,
            user_email: supabaseUser.email || '',
            user_first_name: firstName,
            user_last_name: lastName
          });
          
          // Add random avatar data to the new profile
          if (!createError) {
            await db.updateProfile(supabaseUser.id, { avatar_data: randomAvatar });
          }
          
          if (createError) {
            console.error('AUTH: Failed to create profile:', createError);
          } else {
            // Retry loading the profile immediately with a small delay
            await new Promise(resolve => setTimeout(resolve, 500));
            const { data: retryProfile, error: retryError } = await db.getProfile(supabaseUser.id);
            
            if (retryProfile) {
              setUser(retryProfile);
              return;
            }
          }
          
          // If we reach here, profile creation/loading failed, create a basic user object
          const basicUser: User = {
            id: supabaseUser.id,
            email: supabaseUser.email || '',
            first_name: firstName || 'User',
            last_name: lastName || '',
            avatar_url: supabaseUser.user_metadata?.avatar_url,
            avatar_data: supabaseUser.user_metadata?.avatar_data || null,
            created_at: supabaseUser.created_at,
            updated_at: new Date().toISOString()
          };
          setUser(basicUser);
          return;
        } else {
          // For other errors, create a basic user object as fallback
          const firstName = supabaseUser.user_metadata?.first_name || '';
          const lastName = supabaseUser.user_metadata?.last_name || '';
          const basicUser: User = {
            id: supabaseUser.id,
            email: supabaseUser.email || '',
            first_name: firstName || 'User',
            last_name: lastName || '',
            avatar_url: supabaseUser.user_metadata?.avatar_url,
            avatar_data: supabaseUser.user_metadata?.avatar_data || null,
            created_at: supabaseUser.created_at,
            updated_at: new Date().toISOString()
          };
          setUser(basicUser);
          return;
        }
      }
      
      if (profile) {
        console.log('🔐 AUTH: Loaded profile data:', {
          id: profile.id,
          avatar_url: profile.avatar_url,
          avatar_data: profile.avatar_data,
          hasAvatarData: !!profile.avatar_data,
          hasAvatarUrl: !!profile.avatar_url
        });
        setUser(profile);
      }
    } catch (error: any) {
      console.error('AUTH: Profile loading error:', error);
      // The basic user object is already set, so the app can continue
    }
  };

  const signUp = async (email: string, password: string, firstName: string, lastName: string) => {
    try {
      const { data, error } = await auth.signUp(email, password, firstName, lastName);
      
      if (error) {
        return { error };
      }

      // If email confirmation is disabled, the user will be automatically signed in
      if (data.user && data.session) {
        await loadUserProfile(data.user);
      }

      return { error: null };
    } catch (error: any) {
      return { error };
    }
  };

  const signIn = async (email: string, password: string) => {
    try {
      const { data, error } = await auth.signIn(email, password);
      
      if (error) {
        return { error };
      }

      if (data.user) {
        await loadUserProfile(data.user);
      }

      return { error: null };
    } catch (error: any) {
      return { error };
    }
  };

  const signOut = async () => {
    try {
      // Clear local state immediately to prevent UI delays
      setUser(null);
      setSession(null);
      
      // Attempt to sign out from Supabase
      const { error } = await auth.signOut();
      
      if (error) {
        console.error('AUTH: Supabase sign out error:', error);
      }

      // Always return success for logout to prevent user from being stuck
      // Even if Supabase signOut fails, we've cleared all local state
      return { error: null };
    } catch (error: any) {
      console.error('AUTH: Sign out exception:', error);
      
      // Ensure all local state is cleared even on exception
      setUser(null);
      setSession(null);
      
      // Return success to prevent user from being stuck
      return { error: null };
    }
  };

  const updateProfile = async (updates: Partial<User>) => {
    if (!user) {
      return { error: new Error('No user logged in') };
    }

    try {
      const { data, error } = await db.updateProfile(user.id, updates);
      
      if (error) {
        return { error };
      }

      if (data) {
        setUser(data);
      }

      return { error: null };
    } catch (error: any) {
      return { error };
    }
  };

  const value = {
    user,
    session,
    isAuthenticated: !!session,
    isLoading,
    signUp,
    signIn,
    signOut,
    updateProfile,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuthContext() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuthContext must be used within an AuthProvider');
  }
  return context;
}